import {
  Box,
  Button,
  Flex,
  Icon,
  Switch,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Text,
  TextInput,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@televet/kibble-ui';
import debounce from 'lodash-es/debounce';
import { ViewProps } from 'pages/LoyaltyProgram';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pagination } from 'shared/components/Pagination';
import { LoyaltyEvents } from 'shared/providers/TrackingProvider/EventNames';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  LoyaltyProgramRewardFragment,
  LoyaltyRewardAvailability,
  QueryMode,
  SortOrder,
  useGetLoyaltyProgramBasicInfoQuery,
  useGetLoyaltyProgramRewardsQuery,
  useUpdateLoyaltyProgramRewardsAvailabilityMutation,
} from 'shared/types/graphql';
import { scrollIntoView } from 'shared/utils';
import { RewardModal } from './components/RewardModal';
import { RewardRow } from './components/RewardRow';
import { useTracking } from 'shared/providers/TrackingProvider';

export enum RewardAction {
  Edit = 'Edit',
  Delete = 'Delete',
}

type AvailableSortingFields = 'title' | 'pointCost';

type RewardSorting = {
  field: AvailableSortingFields;
  sortOrder: SortOrder;
};

// The ref property is not used, but throws an error if not passed
// eslint-disable-next-line
const Rewards = forwardRef(({ viewRef }: ViewProps, _ref): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const tableRef = useRef<HTMLDivElement | null>(null);
  const { isOpen: isRewardModalOpen, onOpen: openRewardModal, onClose: closeRewardModal } = useDisclosure();
  const { currentClinicId } = useClinicUser();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [selectedReward, setSelectedReward] = useState<LoyaltyProgramRewardFragment>();
  const [sortBy, setSortBy] = useState<RewardSorting>({ field: 'title', sortOrder: SortOrder.Asc });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(10);
  const { trackEvent } = useTracking();

  const { data: loyaltyProgramSettings } = useGetLoyaltyProgramBasicInfoQuery({
    variables: {
      where: {
        participatingClinics: {
          some: {
            id: {
              equals: currentClinicId,
            },
          },
        },
      },
    },
    skip: !currentClinicId,
  });

  const basicLoyaltyProgramSettings = loyaltyProgramSettings?.findFirstLoyaltyProgram;

  const {
    data: rewardsData,
    loading: loadingRewards,
    refetch: refetchRewards,
  } = useGetLoyaltyProgramRewardsQuery({
    variables: {
      where: {
        title: { contains: debouncedSearchText, mode: QueryMode.Insensitive },
        programId: {
          equals: basicLoyaltyProgramSettings?.id,
        },
        availability: {
          not: {
            equals: LoyaltyRewardAvailability.Archived,
          },
        },
      },
      orderBy: [{ [sortBy.field]: sortBy.sortOrder }],
      take: currentPageSize,
      skip: currentPageSize * (currentPage - 1),
    },
    skip: !basicLoyaltyProgramSettings?.id,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const triggerRefetch = (): void => {
    refetchRewards();
  };

  const rewards = rewardsData?.findManyLoyaltyReward;
  const rewardsCount = rewardsData?.findManyLoyaltyRewardCount || 0;

  const handlePageChange = useCallback((page: number): void => {
    setCurrentPage(page);
  }, []);

  const handlePageSize = useCallback(
    (pageSize: number) => {
      handlePageChange(1);
      setCurrentPageSize(pageSize);
    },
    [handlePageChange, setCurrentPageSize],
  );

  useEffect(() => {
    // Open modal once the selectedRewardId has been updated
    if (selectedReward) {
      openRewardModal();
    }
  }, [openRewardModal, selectedReward]);

  useEffect(() => {
    // Clear selected reward when the modal closes
    if (!isRewardModalOpen) {
      setSelectedReward(undefined);
    }
  }, [isRewardModalOpen]);

  useEffect(() => {
    // Scrolls top of table into view if table is not in view after pagination.
    if (!loadingRewards) {
      if (viewRef.current && tableRef.current) scrollIntoView(viewRef.current, tableRef.current, 500);
    }
  }, [loadingRewards, viewRef]);

  const handleSearchTextChange = useMemo(
    () =>
      debounce(
        (searchText): void => {
          const text = searchText.trim().toLowerCase();
          setDebouncedSearchText(text);
          setCurrentPage(1);
        },
        200,
        { leading: false, trailing: true },
      ),
    [setDebouncedSearchText],
  );

  const handleSearchInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      setSearchText('');
    }
  }, []);

  useEffect(() => {
    handleSearchTextChange(searchText);
  }, [searchText, handleSearchTextChange]);

  const areAllRewardsPublished = useMemo(() => {
    return rewards?.every((reward) => reward.availability === LoyaltyRewardAvailability.Published);
  }, [rewards]);

  const [updateRewardsAvailability, { loading: updatingRewardsAvailability }] =
    useUpdateLoyaltyProgramRewardsAvailabilityMutation();

  const handleAllRewardsSwitch = (): void => {
    trackEvent(LoyaltyEvents.RewardsToggle, { type: 'global' });
    const rewardsIds = rewards?.map((reward) => reward.id);
    updateRewardsAvailability({
      variables: {
        where: {
          id: {
            in: rewardsIds,
          },
        },
        data: {
          availability: areAllRewardsPublished
            ? LoyaltyRewardAvailability.Unpublished
            : LoyaltyRewardAvailability.Published,
        },
      },
    })
      .then(() => {
        toastMessagePop();
        triggerRefetch();
      })
      .catch(() => {
        toastMessagePop(
          'Whoops! There was a problem updating the rewards, please refresh the page and try again.',
          STATUSES.error,
        );
      });
  };

  const handleSorting = (field: AvailableSortingFields): void => {
    setSortBy((currentSortBy) => {
      let newSortOrder = SortOrder.Asc;

      if (field === currentSortBy.field) {
        newSortOrder = currentSortBy.sortOrder === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc;
      }

      return { field, sortOrder: newSortOrder };
    });
  };

  const noResults = useMemo(() => {
    return debouncedSearchText && !loadingRewards && !rewardsCount;
  }, [debouncedSearchText, loadingRewards, rewardsCount]);

  useEffect(() => {
    // If there are no rewards on the current page but there are previous pages, go back one
    if (!rewards?.length && rewardsCount > 0 && currentPage > 1 && !debouncedSearchText) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, debouncedSearchText, handlePageChange, rewards?.length, rewardsCount]);

  return (
    <Box>
      <Flex w="100%" alignItems="center" justifyContent="space-between" mb={3}>
        <TextInput
          name="search"
          placeholder="Search name..."
          value={searchText}
          onChange={(e): void => setSearchText(e.target.value)}
          onKeyDown={handleSearchInputKeyDown}
          leftElement={<Icon name="magnifyingGlass" size="md" />}
          isClearable
          onClear={(): void => setSearchText('')}
          formControlStyle={{ maxW: '320px' }}
        />
        <Button
          iconName="plus"
          iconPosition="left"
          onClick={(): void => {
            trackEvent(LoyaltyEvents.RewardsAddNewClick);
            openRewardModal();
          }}
          size="md"
        >
          Add New
        </Button>
      </Flex>
      <TableContainer ref={tableRef}>
        <Table variant="unstyled" style={{ fontVariantNumeric: 'normal' }}>
          {!rewards?.length && (
            <TableCaption>
              <Text variant="subtle">
                {debouncedSearchText
                  ? `No results found for "${debouncedSearchText}"`
                  : 'There are no rewards, click Add New to continue.'}
              </Text>
            </TableCaption>
          )}
          <Thead>
            <Tr borderBottomWidth="1px" borderColor="border.default">
              <Th p={4}>
                <Flex alignItems="center">
                  <Text size="sm">Name</Text>
                  <Button
                    iconName="sort"
                    variant="ghostNeutral"
                    iconPosition="right"
                    size="xs"
                    onClick={(): void => handleSorting('title')}
                    isDisabled={loadingRewards}
                  />
                </Flex>
              </Th>
              <Th p={4}>
                <Flex alignItems="center">
                  <Text size="sm">Points Value</Text>
                  <Button
                    _focus={{ outline: 'none' }}
                    iconName="sort"
                    variant="ghostNeutral"
                    iconPosition="right"
                    size="xs"
                    onClick={(): void => handleSorting('pointCost')}
                    isDisabled={loadingRewards}
                  />
                </Flex>
              </Th>
              <Th p={4}>
                <Text size="sm">Description</Text>
              </Th>
              <Th p={4}>
                <Flex alignItems="center" justifyContent="flex-end">
                  <Flex alignItems="center" mr={2}>
                    <Text size="sm" textTransform="none" mr={1}>
                      {areAllRewardsPublished ? 'Disable All' : 'Enable All'}
                    </Text>
                    <Tooltip
                      label={`This will ${
                        areAllRewardsPublished ? 'disable' : 'enable'
                      } all rewards in the current page`}
                      titleCaseLabel={false}
                    >
                      <Flex>
                        <Icon name="infoCircle" size="xs" variant="subtle" />
                      </Flex>
                    </Tooltip>
                  </Flex>
                  <Switch
                    isFieldInline
                    formControlStyle={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', w: 'auto' }}
                    isChecked={areAllRewardsPublished}
                    onChange={handleAllRewardsSwitch}
                    isDisabled={updatingRewardsAvailability}
                  />
                </Flex>
              </Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {!!rewards?.length &&
              rewards.map((reward, index) => (
                <RewardRow
                  key={reward.id}
                  reward={reward}
                  setSelectedReward={setSelectedReward}
                  index={index}
                  triggerRefetch={triggerRefetch}
                />
              ))}
          </Tbody>
        </Table>
      </TableContainer>
      {!noResults && (
        <Box mt={5}>
          <Pagination
            recordCount={rewardsCount}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onPageSize={handlePageSize}
            pageSize={currentPageSize}
            isLoading={loadingRewards}
          />
        </Box>
      )}
      {isRewardModalOpen && (
        <RewardModal
          programId={basicLoyaltyProgramSettings?.id || ''}
          selectedReward={selectedReward}
          isOpen={isRewardModalOpen}
          onClose={closeRewardModal}
          triggerRefetch={triggerRefetch}
        />
      )}
    </Box>
  );
});

Rewards.displayName = 'Rewards';

export default Rewards;
